<script setup>
import { computed } from 'vue';
import NotificationItem from './NotificationItem.vue';

// Tabs (when the role has two kinds of alerts), the list and an empty
// state. Used by the bell's popover (limited, compact) and the full list.
const props = defineProps({
  items: { type: Array, required: true },
  tabs: { type: Array, default: () => [] }, // [{ value, label, empty, hint }]
  limit: { type: Number, default: 0 }, // 0 = all
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['open']);
const tab = defineModel('tab', { type: String, default: '' });

const activeTab = computed(
  () => props.tabs.find((t) => t.value === tab.value) || props.tabs[0] || null,
);
const inTab = computed(() =>
  activeTab.value ? props.items.filter((i) => i.category === activeTab.value.value) : props.items,
);
const visible = computed(() => (props.limit ? inTab.value.slice(0, props.limit) : inTab.value));
const unreadIn = (value) => props.items.filter((i) => i.category === value && !i.read).length;
</script>

<template>
  <div class="feed">
    <div v-if="tabs.length" class="feed__tabs" role="tablist">
      <button
        v-for="t in tabs"
        :key="t.value"
        type="button"
        role="tab"
        class="feed__tab"
        :class="{ 'feed__tab--active': activeTab?.value === t.value }"
        :aria-selected="activeTab?.value === t.value"
        @click="tab = t.value"
      >
        {{ t.label }}
        <span v-if="unreadIn(t.value)" class="feed__count">{{ unreadIn(t.value) }}</span>
      </button>
    </div>

    <div v-if="visible.length" class="feed__list">
      <NotificationItem
        v-for="item in visible"
        :key="item.key"
        :item="item"
        :compact="compact"
        @open="emit('open', $event)"
      />
    </div>

    <div v-else class="feed__empty">
      <v-icon size="24">mdi-bell-check-outline</v-icon>
      <p class="feed__empty-title">{{ activeTab?.empty || 'No notifications' }}</p>
      <p class="feed__empty-hint">{{ activeTab?.hint || "You're all caught up." }}</p>
    </div>
  </div>
</template>

<style scoped>
.feed {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.feed__tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 10px;
  background: var(--accent);
}
.feed__tab {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted-foreground);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.feed__tab:hover {
  color: var(--foreground);
}
.feed__tab--active {
  background: var(--background);
  color: var(--foreground);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.feed__tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}
.feed__count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 11px;
  line-height: 18px;
}
.feed__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.feed__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  text-align: center;
  color: var(--faint-foreground);
}
.feed__empty-title {
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--foreground);
}
.feed__empty-hint {
  margin: 2px 0 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
</style>
